import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { useMode } from '../contexts/ModeContext';
import { ArrowLeft, Volume2, RotateCcw, Trophy, CheckCircle2, XCircle, Sparkles } from 'lucide-react';

const ttsUrl = (text) => {
  const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
  return `${base}/api/elifba/tts?text=${encodeURIComponent(text)}`;
};

const HAREKE_OPTIONS = [
  { value: 'none', label: 'Harf İsimleri' },
  { value: 'fatha', label: 'Üstün (Fetha)' },
  { value: 'kasra', label: 'Esre (Kesra)' },
  { value: 'damma', label: 'Ötre (Damme)' },
];

// =============================================================================
// MATCH GAME
// =============================================================================
const MatchGame = ({ kids, hareke }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({ arabic: null, label: null });
  const [matched, setMatched] = useState(new Set()); // pair_ids that are matched
  const [score, setScore] = useState(0);
  const [tries, setTries] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const audioRef = useRef(null);

  const loadGame = async () => {
    setLoading(true);
    setSelected({ arabic: null, label: null });
    setMatched(new Set());
    setScore(0);
    setTries(0);
    setSubmitted(false);
    try {
      const res = await apiClient.get('/elifba/games/match', { params: { pairs: 6, hareke } });
      setData(res.data);
    } catch {
      toast.error('Oyun yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hareke]);

  const playSound = (text) => {
    if (!text) return;
    if (audioRef.current) audioRef.current.pause();
    audioRef.current = new Audio(ttsUrl(text));
    audioRef.current.play().catch(() => {});
  };

  const handleClick = (kind, card) => {
    if (matched.has(card.pair_id)) return;
    const next = { ...selected, [kind]: card };
    setSelected(next);
    if (kind === 'arabic') playSound(card.tts_text);

    if (next.arabic && next.label) {
      setTries((t) => t + 1);
      if (next.arabic.pair_id === next.label.pair_id) {
        // Match!
        const newMatched = new Set(matched);
        newMatched.add(next.arabic.pair_id);
        setMatched(newMatched);
        setScore((s) => s + 1);
        toast.success('Doğru! ✓', { duration: 700 });
        setSelected({ arabic: null, label: null });
      } else {
        // Wrong
        setTimeout(() => setSelected({ arabic: null, label: null }), 600);
      }
    }
  };

  // Submit final score on completion
  useEffect(() => {
    if (data && matched.size === data.pairs && !submitted) {
      setSubmitted(true);
      apiClient
        .post(`/elifba/games/score?game=match&score=${score + 1}&total=${data.pairs}`)
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched, data]);

  if (loading) {
    return (
      <div className="p-8 text-center" data-testid="match-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-700 mx-auto"></div>
      </div>
    );
  }
  if (!data) return null;

  const done = matched.size === data.pairs;

  const cardClass = (kind, card) => {
    const isMatched = matched.has(card.pair_id);
    const isSelected = selected[kind]?.pair_id === card.pair_id && !isMatched;
    if (isMatched) {
      return kids
        ? 'bg-gradient-to-br from-green-200 to-emerald-200 border-2 border-green-400 opacity-50'
        : 'bg-emerald-50 border border-emerald-300 opacity-50';
    }
    if (isSelected) {
      return kids
        ? 'bg-gradient-to-br from-amber-200 to-yellow-200 border-2 border-amber-400 scale-105'
        : 'bg-amber-50 border-2 border-amber-400 scale-105';
    }
    return kids
      ? 'bg-white border-2 border-slate-200 hover:border-emerald-300'
      : 'bg-white border border-stone-200 hover:border-emerald-400 hover:shadow-md';
  };

  return (
    <div className="space-y-6" data-testid="match-game">
      {/* HUD */}
      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-600" />
            <span className="font-bold text-stone-800" data-testid="match-score">{matched.size}/{data.pairs}</span>
          </div>
          <div className="text-sm text-stone-500">Deneme: {tries}</div>
        </div>
        <button
          onClick={loadGame}
          className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700"
          data-testid="match-restart"
        >
          <RotateCcw className="w-4 h-4" />
          Yenile
        </button>
      </div>

      {/* Win banner */}
      {done && (
        <div className="p-6 bg-gradient-to-r from-amber-100 to-yellow-100 border-2 border-amber-300 rounded-2xl text-center" data-testid="match-win">
          <Sparkles className="w-10 h-10 text-amber-500 mx-auto mb-2" />
          <h3 className="text-2xl font-bold text-amber-800">Tebrikler!</h3>
          <p className="text-stone-700">Tüm eşleşmeleri buldun ({tries} denemede)</p>
        </div>
      )}

      {/* Two columns */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className={`text-sm font-bold mb-3 text-center text-stone-600`}>Arapça</h3>
          <div className="space-y-2" dir="rtl">
            {data.arabic_cards.map((card) => (
              <button
                key={`a-${card.pair_id}`}
                onClick={() => handleClick('arabic', card)}
                disabled={matched.has(card.pair_id)}
                className={`w-full p-4 rounded-xl transition-all flex items-center justify-between ${cardClass('arabic', card)}`}
                data-testid={`match-arabic-${card.pair_id}`}
              >
                <Volume2 className="w-4 h-4 text-emerald-600" />
                <div className="text-3xl font-arabic" dir="rtl">{card.value}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className={`text-sm font-bold mb-3 text-center text-stone-600`}>Türkçe Okunuşu</h3>
          <div className="space-y-2">
            {data.label_cards.map((card) => (
              <button
                key={`l-${card.pair_id}`}
                onClick={() => handleClick('label', card)}
                disabled={matched.has(card.pair_id)}
                className={`w-full p-4 rounded-xl transition-all text-center ${cardClass('label', card)}`}
                data-testid={`match-label-${card.pair_id}`}
              >
                <div className="text-lg font-semibold text-stone-800">{card.value}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// QUIZ
// =============================================================================
const Quiz = ({ kids, hareke }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const [finished, setFinished] = useState(false);
  const audioRef = useRef(null);

  const loadGame = async () => {
    setLoading(true);
    setIdx(0);
    setScore(0);
    setPicked(null);
    setFinished(false);
    try {
      const res = await apiClient.get('/elifba/games/quiz', { params: { count: 8, hareke } });
      setData(res.data);
    } catch {
      toast.error('Quiz yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hareke]);

  const playSound = (text) => {
    if (!text) return;
    if (audioRef.current) audioRef.current.pause();
    audioRef.current = new Audio(ttsUrl(text));
    audioRef.current.play().catch(() => {});
  };

  const pickAnswer = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const q = data.questions[idx];
    if (i === q.correct_index) {
      setScore((s) => s + 1);
      toast.success('Doğru ✓', { duration: 800 });
    } else {
      toast.error(`Doğru cevap: ${q.options[q.correct_index]}`, { duration: 1200 });
    }
  };

  const next = () => {
    if (idx + 1 >= data.questions.length) {
      setFinished(true);
      const finalScore = picked === data.questions[idx].correct_index ? score : score;
      apiClient
        .post(`/elifba/games/score?game=quiz&score=${finalScore}&total=${data.questions.length}`)
        .catch(() => {});
    } else {
      setIdx((i) => i + 1);
      setPicked(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center" data-testid="quiz-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-700 mx-auto"></div>
      </div>
    );
  }
  if (!data) return null;

  if (finished) {
    const pct = Math.round((score / data.questions.length) * 100);
    return (
      <div className="p-8 text-center bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200" data-testid="quiz-finished">
        <Trophy className="w-16 h-16 text-amber-500 mx-auto mb-3" />
        <h3 className="text-3xl font-bold text-emerald-800 mb-2">Bitirdin!</h3>
        <p className="text-xl text-stone-700 mb-4">{score} / {data.questions.length} doğru — %{pct}</p>
        <button
          onClick={loadGame}
          className="px-6 py-3 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 font-semibold flex items-center gap-2 mx-auto"
          data-testid="quiz-restart"
        >
          <RotateCcw className="w-4 h-4" />
          Yeni Quiz
        </button>
      </div>
    );
  }

  const q = data.questions[idx];
  return (
    <div className="space-y-6" data-testid="quiz-game">
      {/* HUD */}
      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-stone-200 shadow-sm">
        <span className="text-sm font-medium text-stone-700">
          Soru <span data-testid="quiz-position">{idx + 1}</span>/{data.questions.length}
        </span>
        <span className="text-sm font-bold text-emerald-700" data-testid="quiz-score">Doğru: {score}</span>
      </div>

      {/* Question */}
      <div className={`p-8 text-center ${
        kids
          ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.08)]'
          : 'bg-white rounded-xl border border-stone-200 shadow-sm'
      }`}>
        <p className="text-sm text-stone-500 mb-3">Bu harfin/sesin Türkçe okunuşu nedir?</p>
        <div className="text-7xl font-arabic mb-4 leading-none" dir="rtl" data-testid="quiz-prompt">{q.prompt}</div>
        <button
          onClick={() => playSound(q.tts_text)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
          data-testid="quiz-listen"
        >
          <Volume2 className="w-4 h-4" />
          Dinle
        </button>
      </div>

      {/* Options */}
      <div className="grid grid-cols-2 gap-3">
        {q.options.map((opt, i) => {
          const isCorrect = picked !== null && i === q.correct_index;
          const isWrong = picked === i && i !== q.correct_index;
          let cls = 'bg-white border-stone-200 hover:border-emerald-400 text-stone-800';
          if (isCorrect) cls = 'bg-emerald-100 border-emerald-500 text-emerald-900';
          else if (isWrong) cls = 'bg-rose-100 border-rose-500 text-rose-900';
          else if (picked !== null) cls = 'bg-white border-stone-200 text-stone-400 opacity-60';
          return (
            <button
              key={i}
              onClick={() => pickAnswer(i)}
              disabled={picked !== null}
              className={`p-4 rounded-xl border-2 text-lg font-semibold transition-all flex items-center justify-between ${cls}`}
              data-testid={`quiz-option-${i}`}
            >
              <span>{opt}</span>
              {isCorrect && <CheckCircle2 className="w-5 h-5" />}
              {isWrong && <XCircle className="w-5 h-5" />}
            </button>
          );
        })}
      </div>

      {/* Next */}
      {picked !== null && (
        <div className="flex justify-center">
          <button
            onClick={next}
            className="px-8 py-3 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 font-semibold"
            data-testid="quiz-next"
          >
            {idx + 1 >= data.questions.length ? 'Bitir' : 'Sonraki Soru'}
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN
// =============================================================================
function ElifBaGames() {
  const navigate = useNavigate();
  const { isKidsMode } = useMode();
  const [tab, setTab] = useState('match');
  const [hareke, setHareke] = useState('none');

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #FEF3C7 0%, #DBEAFE 50%, #E9D5FF 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)',
      }}
      data-testid="games-page"
    >
      <header className="px-6 py-4 bg-white/60 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/elifba')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            data-testid="games-back"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Bölümler</span>
          </button>
          <h1 className={`text-2xl font-bold ${isKidsMode ? 'font-kids text-green-600' : 'font-elegant text-emerald-900'}`}>
            Elif-Ba Oyunları
          </h1>
          <div className="w-24"></div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 p-1 bg-stone-100 rounded-xl">
          {[
            { value: 'match', label: 'Eşleştirme' },
            { value: 'quiz', label: 'Quiz' },
          ].map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
                tab === t.value
                  ? 'bg-white shadow-sm text-emerald-700'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
              data-testid={`games-tab-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Hareke selector */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-stone-700 mb-2">Mod seç:</label>
          <div className="flex flex-wrap gap-2">
            {HAREKE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setHareke(opt.value)}
                className={`px-4 py-2 text-sm rounded-lg font-medium transition ${
                  hareke === opt.value
                    ? 'bg-emerald-700 text-white'
                    : 'bg-white border border-stone-200 text-stone-700 hover:border-emerald-300'
                }`}
                data-testid={`games-hareke-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'match' ? (
          <MatchGame kids={isKidsMode} hareke={hareke} />
        ) : (
          <Quiz kids={isKidsMode} hareke={hareke} />
        )}
      </div>
    </div>
  );
}

export default ElifBaGames;
